/**
 * The ledger — the main screen, and the sheet replacement.
 *
 * Filters live in the URL, so any view is shareable. Keyboard: `/` search, `n` new,
 * `j`/`k` move, Enter open, `e` edit, Escape close.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { useConfirm } from "../../components/ui/modal";
import { Spinner } from "../../components/ui/spinner";
import { useBalances, useGroup, useGroupId } from "../../components/layout/AppShell";
import { parseFilters, serializeFilters, type LedgerFilters } from "../../lib/filters";
import { cn } from "../../lib/utils";
import { api, type Transaction } from "../../lib/api";
import { EntryDrawer } from "./EntryDrawer";
import { FilterBar } from "./FilterBar";
import { LedgerTable } from "./LedgerTable";
import { useCategories, useLedger, useMembers } from "./useLedger";

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768,
  );
  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const listener = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    setIsMobile(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, []);
  return isMobile;
}

export function LedgerPage() {
  const groupId = useGroupId();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const group = useGroup(groupId);
  const balances = useBalances(groupId);
  const members = useMembers(groupId);
  const categories = useCategories(groupId);
  const ledger = useLedger(groupId, filters);

  const [sheetMode, setSheetMode] = useState(() => searchParams.get("view") === "sheet");
  const [activeIndex, setActiveIndex] = useState(-1);
  // `?entry=123` opens that entry's detail on arrival. It is how confirming a proposal in
  // the assistant lands you on the thing you just added rather than on a card telling you
  // it exists somewhere. Kept in the URL so it survives a reload and can be linked to.
  const [openId, setOpenId] = useState<number | null>(
    () => Number(searchParams.get("entry")) || null,
  );

  // The initializer above covers arriving on a fresh mount, which is the normal path from
  // the assistant. This covers the case where the ledger is already mounted and the param
  // changes underneath it. Only ever *opens* — clicking a row sets `openId` without
  // touching the URL, and this must not close that again.
  const entryParam = searchParams.get("entry");
  useEffect(() => {
    const requested = Number(entryParam) || null;
    if (requested !== null) setOpenId(requested);
  }, [entryParam]);
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);

  const transactions = useMemo(
    () => (ledger.data?.pages ?? []).flatMap((page) => page.items),
    [ledger.data],
  );
  const totals = ledger.data?.pages[0];
  const currency = group.data?.base_currency ?? "USD";
  const isAdmin = group.data?.my_role === "admin";

  const categoryNames = useMemo(
    () => new Map((categories.data ?? []).map((category) => [category.id, category.name])),
    [categories.data],
  );

  const applyFilters = useCallback(
    (next: LedgerFilters) => {
      const params = serializeFilters(next);
      if (sheetMode) params.set("view", "sheet");
      setSearchParams(params, { replace: true });
    },
    [setSearchParams, sheetMode],
  );

  const changeSheetMode = useCallback(
    (value: boolean) => {
      setSheetMode(value);
      const params = serializeFilters(filters);
      if (value) params.set("view", "sheet");
      setSearchParams(params, { replace: true });
    },
    [filters, setSearchParams],
  );

  const openTransaction = useCallback((transaction: Transaction) => {
    setOpenId(transaction.id);
  }, []);

  // What the checkboxes are for: lock, unlock, or delete a run of entries in one go.
  // Admin-only, and the same endpoints the single-entry actions use.
  const queryClient = useQueryClient();
  const [askBulk, bulkConfirm] = useConfirm();
  const afterBulk = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["transactions", groupId] });
    void balances.refetch();
    setSelection(new Set());
  }, [balances, groupId, queryClient]);

  const bulk = useMutation({
    mutationFn: (locked: boolean) =>
      api.post(`/groups/${groupId}/transactions/locked`, {
        transaction_ids: [...selection],
        locked,
      }),
    onSuccess: afterBulk,
  });

  const bulkDelete = useMutation({
    mutationFn: async () => {
      for (const id of selection) {
        await api.delete(`/groups/${groupId}/transactions/${id}`);
      }
    },
    onSuccess: afterBulk,
  });

  // Keyboard shortcuts (DESIGN.md §12.3). Ignored while typing in a field.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);

      if (event.key === "Escape") {
        setOpenId(null);
        return;
      }
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "/") {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (event.key === "n") {
        event.preventDefault();
        navigate(`/g/${groupId}/new`);
        return;
      }
      if (event.key === "j" || event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, transactions.length - 1));
        return;
      }
      if (event.key === "k" || event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        return;
      }
      if (event.key === "Enter" && activeIndex >= 0) {
        const transaction = transactions[activeIndex];
        if (transaction) setOpenId(transaction.id);
        return;
      }
      if (event.key === "e" && activeIndex >= 0) {
        const transaction = transactions[activeIndex];
        if (transaction?.can_edit) navigate(`/g/${groupId}/t/${transaction.id}/edit`);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, groupId, navigate, transactions]);

  const exportHref = useMemo(() => {
    const params = serializeFilters(filters);
    params.set("view", sheetMode ? "matrix" : "rows");
    return `/api/groups/${groupId}/export.csv?${params}`;
  }, [filters, groupId, sheetMode]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {bulkConfirm}
      <div className="flex items-center justify-between gap-3 pt-2">
        <h1 className="display text-sm text-muted">Ledger</h1>
        {/* One row, one place to look: add an entry, or change how the ledger is drawn. */}
        <div className="hidden items-center gap-2 md:flex">
          <button
            type="button"
            onClick={() => changeSheetMode(!sheetMode)}
            aria-pressed={sheetMode}
            title="Sheet mode: one column per member"
            className={cn("chip", sheetMode && "chip-on")}
          >
            {sheetMode ? "⊟ Sheet" : "⊞ Sheet"}
          </button>
          <Button variant="primary" onClick={() => navigate(`/g/${groupId}/new`)}>
            + Add entry
          </Button>
        </div>
      </div>

      <FilterBar
        filters={filters}
        onChange={applyFilters}
        members={members.data ?? []}
        categories={categories.data ?? []}
        currency={currency}
        totalCount={totals?.total_count ?? 0}
        totalCents={totals?.total_base_cents ?? 0}
        searchRef={searchRef}
        exportHref={exportHref}
        isMobile={isMobile}
        isAdmin={Boolean(isAdmin)}
      />

      {selection.size > 0 && isAdmin ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-accent/40 bg-accent/5 px-3 py-2 text-sm">
          <span className="font-medium">
            {selection.size} selected
          </span>
          <Button size="sm" disabled={bulk.isPending} onClick={() => bulk.mutate(true)}>
            Lock
          </Button>
          <Button size="sm" disabled={bulk.isPending} onClick={() => bulk.mutate(false)}>
            Unlock
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={bulkDelete.isPending}
            onClick={() =>
              askBulk({
                title: `Delete ${selection.size} ${selection.size === 1 ? "entry" : "entries"}?`,
                body: "Balances update immediately. Deleted entries stay visible under “Show deleted”, so this can be undone.",
                confirmLabel: "Delete",
                destructive: true,
                onConfirm: () => bulkDelete.mutate(),
              })
            }
          >
            Delete
          </Button>
          <Button size="sm" className="ml-auto" onClick={() => setSelection(new Set())}>
            Clear
          </Button>
        </div>
      ) : null}

      {ledger.isPending ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : transactions.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
          <p className="text-muted">Nothing here yet.</p>
          <Button variant="primary" onClick={() => navigate(`/g/${groupId}/new`)}>
            Add the first entry
          </Button>
        </div>
      ) : (
        <LedgerTable
          transactions={transactions}
          members={members.data ?? []}
          currency={currency}
          myMemberId={group.data?.my_member_id}
          categoryNames={categoryNames}
          sheetMode={sheetMode && !isMobile}
          activeIndex={activeIndex}
          selection={selection}
          canSelect={Boolean(isAdmin) && !isMobile && !sheetMode}
          onActivate={setActiveIndex}
          onOpen={openTransaction}
          onToggleSelect={(id) =>
            setSelection((current) => {
              const next = new Set(current);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            })
          }
          onReachEnd={() => {
            if (ledger.hasNextPage && !ledger.isFetchingNextPage) void ledger.fetchNextPage();
          }}
          isMobile={isMobile}
        />
      )}

      {ledger.isFetchingNextPage ? (
        <div className="flex justify-center py-2">
          <Spinner />
        </div>
      ) : null}

      <EntryDrawer
        groupId={groupId}
        transactionId={openId}
        members={members.data ?? []}
        currency={currency}
        categoryNames={categoryNames}
        balancesRefetch={balances.refetch}
        onClose={() => {
          setOpenId(null);
          // Drop `?entry=` too, or reloading — or pressing back — reopens the drawer on
          // a page the person has already closed it on.
          if (searchParams.has("entry")) {
            const next = new URLSearchParams(searchParams);
            next.delete("entry");
            setSearchParams(next, { replace: true });
          }
        }}
      />
    </div>
  );
}
